/* eslint-disable max-lines */
import { CellClickedEvent, GridApi, IRowNode, ValueSetterParams } from '@ag-grid-community/core';
import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { cloneDeep, flatten, isString, set, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, merge } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { ApiService } from '@shared/services/api.service';
import {
  AmountType,
  Currency,
  DataSource,
  DocumentType,
  EntityType,
  EventType,
  GqlService,
  InvoiceStatus,
  listUserNamesWithEmailQuery,
  MonthlyExchangeRate,
  Note,
  NoteType,
  PaymentStatus,
  User,
  WorkflowDetail,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { BudgetCurrencyType } from '@shared/store/budget-currency/budget-currency.model';
import { BudgetCurrencyQuery } from '@shared/store/budget-currency/budget-currency.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { PurchaseOrdersService } from '@shared/store/purchase-orders/purchase-orders.service';
import { Option } from '@shared/types/components.type';
import { decimalAddAll, decimalEquality } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { InvoiceCard, InvoiceModel } from './invoice.model';
import { InvoiceQuery } from './invoice.query';
import { InvoiceStore } from './invoice.store';

interface InvoiceFormGroup {
  accrualPeriod: FormControl<null | string[]>;
  does_invoice_mappings_match_total: FormControl<null | string>;
  has_prepaid: FormControl<null | string>;
  invoiceDate: FormControl<null | string>;
  is_deposit: FormControl<null | string>;
  purchase_order: FormControl<null | string[]>;
  search: FormControl<null | string>;
  services_period: FormControl<null | string[]>;
  vendors: FormControl<null | string[]>;
}

const getCostAsNumber = (cost: number | string) => Number(Number(cost).toFixed(2));

export const isInvoiceTotalEqual = (data: InvoiceModel) => {
  const invoice_total = getCostAsNumber(data.expense_amounts.invoice_total.value);

  const item_totals = decimalAddAll(
    15,
    getCostAsNumber(data.expense_amounts.services_total.value),
    getCostAsNumber(data.expense_amounts.discount_total.value),
    getCostAsNumber(data.expense_amounts.investigator_total.value),
    getCostAsNumber(data.expense_amounts.pass_thru_total.value)
  );

  return decimalEquality(invoice_total, item_totals, 2);
};

@Injectable({ providedIn: 'root' })
export class InvoiceService {
  authQuery = inject(AuthQuery);
  eventService = inject(EventService);
  invoiceQuery = inject(InvoiceQuery);
  organizationQuery = inject(OrganizationQuery);
  organizationService = inject(OrganizationService);
  purchaseOrdersQuery = inject(PurchaseOrdersQuery);
  purchaseOrdersService = inject(PurchaseOrdersService);

  private apiService = inject(ApiService);
  private budgetCurrencyQuery = inject(BudgetCurrencyQuery);
  private gqlService = inject(GqlService);
  private invoiceStore = inject(InvoiceStore);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private router = inject(Router);

  selectedPurchaseOrder = signal<string[]>([]);
  selectedServicePeriodOptions = signal<string[]>([]);
  showInvalidCostCategorization = signal<boolean>(false);
  showRequireAccrualPeriod = signal<boolean>(false);
  showRequireCostBreakdown = signal<boolean>(false);
  showUnpaidInvoices = signal<boolean>(false);

  accrualPeriodOptions: Option[] = [];
  filtersForm = new FormGroup<InvoiceFormGroup>({
    accrualPeriod: new FormControl([] as string[]),
    does_invoice_mappings_match_total: new FormControl(null),
    has_prepaid: new FormControl(null),
    invoiceDate: new FormControl(''),
    is_deposit: new FormControl(null),
    purchase_order: new FormControl([] as string[]),
    search: new FormControl(''),
    services_period: new FormControl([] as string[]),
    vendors: new FormControl([] as string[]),
  });
  gridAPI!: GridApi;
  invoiceDesignationChanged$ = new BehaviorSubject(false);
  invoiceStatusOptions = [
    InvoiceStatus.STATUS_PENDING_REVIEW,
    InvoiceStatus.STATUS_PENDING_APPROVAL,
    InvoiceStatus.STATUS_APPROVED,
    InvoiceStatus.STATUS_DECLINED,
  ];
  invoiceUrl = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`;
  newInvoiceCreated$ = new BehaviorSubject(false);
  paymentStatusOptions = [
    null,
    PaymentStatus.PAYMENT_STATUS_PAID_IN_FULL,
    PaymentStatus.PAYMENT_STATUS_UNPAID,
  ];
  purchaseOrderOptions: Option[] = [];
  servicePeriodOptions: Option[] = [];
  trialEndDate = '';
  trialMonthClose = '';
  trialStartDate = '';
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();
  vendorOptions: Option[] = [];

  async add({
    bucket_keys,
    id,
    organization_id,
    po_reference,
  }: {
    bucket_keys: string[];
    id: string;
    organization_id: string;
    po_reference: null | string;
  }) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createInvoice$({ bucket_keys, id, organization_id, po_reference })
    );
    let invoice: InvoiceModel | null = null;

    if (success && data) {
      const { workflow_details } = data;

      invoice = {
        ...data,
        ...{
          cards: this.workflowToCards(workflow_details as WorkflowDetail[]),
          expense_amounts: this.mapExpenseAmountToObject(data.expense_amounts),
          po_reference: data.purchase_order_id,
        },
      } as unknown as InvoiceModel;

      this.invoiceStore.add(invoice);
      this.newInvoiceCreated$.next(true);
    }

    return { data: invoice, errors, success };
  }

  // eslint-disable-next-line max-lines-per-function
  calculateTotals = () => {
    const rowsToCalculateAgainst = this.gridAPI
      ?.getRenderedNodes()
      .filter((row) => row.level === 0);

    const uniqVendorCurrencies = uniq(
      rowsToCalculateAgainst?.map((x) => x.data?.organization.currency)
    );

    const isVendorSelected =
      this.budgetCurrencyQuery.getValue().currency === BudgetCurrencyType.VENDOR;

    let currency = Currency.USD;

    // if there's only one currency, we can use it for formatting
    if (uniqVendorCurrencies.length === 1) {
      currency = uniqVendorCurrencies[0];
    }

    // if there are multiple currencies and we're toggled on vendor currency, we don't need to calculate a total row since we can't directly sum different currencies
    if (uniqVendorCurrencies.length > 1 && isVendorSelected) {
      return {
        expense_amounts: {
          invoice_total: { value: 0 },
          invoice_total_trial_currency: { value: 0 },
        },
      };
    } else {
      return rowsToCalculateAgainst?.reduce(
        (accumulator, row) => ({
          expense_amounts: {
            discount_total: {
              currency,
              value:
                accumulator.expense_amounts.discount_total.value +
                (uniqVendorCurrencies.length > 1
                  ? 0
                  : row.data?.expense_amounts.discount_total.value),
            },
            discount_total_trial_currency: {
              value:
                accumulator.expense_amounts.discount_total_trial_currency.value +
                row.data?.expense_amounts.discount_total_trial_currency.value,
            },
            investigator_total: {
              currency,
              value:
                accumulator.expense_amounts.investigator_total.value +
                (uniqVendorCurrencies.length > 1
                  ? 0
                  : row.data?.expense_amounts.investigator_total.value),
            },
            investigator_total_trial_currency: {
              value:
                accumulator.expense_amounts.investigator_total_trial_currency.value +
                row.data?.expense_amounts.investigator_total_trial_currency.value,
            },
            invoice_total: {
              currency,
              value:
                accumulator.expense_amounts.invoice_total.value +
                (uniqVendorCurrencies.length > 1
                  ? 0
                  : row.data?.expense_amounts.invoice_total.value),
            },
            invoice_total_trial_currency: {
              value:
                accumulator.expense_amounts.invoice_total_trial_currency.value +
                row.data?.expense_amounts.invoice_total_trial_currency.value,
            },
            pass_thru_total: {
              currency,
              value:
                accumulator.expense_amounts.pass_thru_total.value +
                (uniqVendorCurrencies.length > 1
                  ? 0
                  : row.data?.expense_amounts.pass_thru_total.value),
            },
            pass_thru_total_trial_currency: {
              value:
                accumulator.expense_amounts.pass_thru_total_trial_currency.value +
                row.data?.expense_amounts.pass_thru_total_trial_currency.value,
            },
            services_total: {
              currency,
              value:
                accumulator.expense_amounts.services_total.value +
                (uniqVendorCurrencies.length > 1
                  ? 0
                  : row.data?.expense_amounts.services_total.value),
            },
            services_total_trial_currency: {
              value:
                accumulator.expense_amounts.services_total_trial_currency.value +
                row.data?.expense_amounts.services_total_trial_currency.value,
            },
          },
        }),
        {
          expense_amounts: {
            discount_total: { currency: Currency.USD, value: 0 },
            discount_total_trial_currency: { value: 0 },
            investigator_total: { currency: Currency.USD, value: 0 },
            investigator_total_trial_currency: { value: 0 },
            invoice_total: { currency: Currency.USD, value: 0 },
            invoice_total_trial_currency: { value: 0 },
            pass_thru_total: { currency: Currency.USD, value: 0 },
            pass_thru_total_trial_currency: { value: 0 },
            services_total: { currency: Currency.USD, value: 0 },
            services_total_trial_currency: { value: 0 },
          },
        }
      );
    }
  };

  constructor() {
    this.filtersForm.setValue({
      accrualPeriod: [],
      does_invoice_mappings_match_total: null,
      has_prepaid: null,
      invoiceDate: '',
      is_deposit: null,
      purchase_order: [],
      search: '',
      services_period: [],
      vendors: [],
    });
    this.resetFiltersAfterTrialChange();
    this.initTrialChanges()
      .pipe(takeUntilDestroyed())
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async () => {
        if (this.isInInvoiceDetail(this.router.url)) {
          await this.router.navigateByUrl(this.invoiceUrl);
        }
      });
  }

  async createInvoiceNote(invoice: InvoiceModel, message: string) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createNote$({
        entity_id: invoice.id,
        entity_type: EntityType.INVOICE,
        message: message || '',
        note_type: NoteType.NOTE_TYPE_GENERAL,
      })
    );

    if (success && data) {
      this.overlayService.success('Notes saved');
      this.invoiceStore.update(invoice.id, ({ notes }) => {
        return {
          notes: [...(notes || []), data as Note],
        };
      });

      return data;
    } else {
      this.overlayService.error(errors);

      return;
    }
  }

  async downloadInvoiceItems(rowNode: IRowNode) {
    let { bucket_key } = rowNode.data?.file || {};

    if (!bucket_key) {
      const trialId = this.mainQuery.getValue().trialKey;

      await this.apiService
        .getFilesByFilters(
          `trials/${trialId}/vendors/`,
          undefined,
          EntityType.INVOICE,
          DocumentType.DOCUMENT_INVOICE,
          rowNode.data?.id
        )
        .then((documents) => {
          bucket_key = documents[0].bucket_key;
        });

      if (!bucket_key) {
        this.overlayService.error(MessagesConstants.INVOICE.DOES_NOT_HAVE_FILE);

        return;
      }
    }

    const pathParts = bucket_key.split('/');
    const formattedKey = `${pathParts.slice(0, pathParts.indexOf('invoices') + 2).join('/')}/`;
    const ref = this.overlayService.loading();
    const { data, success } = await this.apiService.getS3ZipFile(formattedKey);

    if (success && data) {
      const fileName =
        `${rowNode.data.invoice_no}_${rowNode.data.organization.name}_Invoice_` +
        `${rowNode.data.id}_${(rowNode.data.create_date || '').slice(0, 10)}`;

      await this.apiService.downloadZipOrFile(data, fileName);
    }

    ref.close();
  }

  downloadInvoiceLines(rowNode: IRowNode) {
    const trialShortName = this.mainQuery.getSelectedTrial()?.short_name;

    if (rowNode.data.line_items.length > 0) {
      const blob = new Blob([
        this.invoiceLinesToCsv(rowNode.data.line_items, rowNode.data.data_source_id),
      ]);
      const fileName = `${rowNode.data.invoice_no}_${rowNode.data.organization.name}_${trialShortName}_Auxilius${Utils.getIntegrationSourceFileNaming(rowNode.data.data_source_id)}_Invoice_Line_Items.csv`;

      this.apiService.downloadBlob(blob, fileName);
    }

    if (rowNode.data.ocr_line_items.length > 0) {
      const blob = new Blob([
        this.invoiceLinesToCsv(rowNode.data.ocr_line_items, DataSource.DATA_SOURCE_AUXILIUS),
      ]);
      const fileName = `${rowNode.data.invoice_no}_${rowNode.data.organization.name}_${trialShortName}_Auxilius_Parsed_Invoice_Line_Items.csv`;

      this.apiService.downloadBlob(blob, fileName);
    }
  }

  generatePinnedRow() {
    const totals = this.calculateTotals();

    // if the invoice total is 0, then no need for a total row
    if (
      totals?.expense_amounts?.invoice_total.value === 0 &&
      totals?.expense_amounts?.invoice_total_trial_currency.value === 0
    ) {
      this.gridAPI?.setGridOption('pinnedBottomRowData', []);
    } else {
      this.gridAPI?.setGridOption('pinnedBottomRowData', [
        {
          ...totals,
          created_by: '',
          file: '',
          invoice_no: 'Total',
          organization: { name: Utils.zeroHyphen },
        },
      ]);
    }
  }

  getAccrualPeriodOptions(startDate: string, endDate: string) {
    const startMonth = dayjs(startDate).startOf('month');
    const endMonth = dayjs(endDate).startOf('month');
    const monthCount = Math.max(0, endMonth.diff(startMonth, 'month')) + 1;

    const accrualPeriodOptions: Option[] = [];

    for (let monthIndex = 0; monthIndex < monthCount; monthIndex++) {
      const date = startMonth.add(monthIndex, 'month');

      accrualPeriodOptions.push({
        label: date.format('MMMM YYYY'),
        value: `${date.format('YYYY-MM')}-01`,
      });
    }

    return accrualPeriodOptions;
  }

  getFilePath(invoice_id: string, vendorId: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/vendors/${vendorId}/invoices/${invoice_id}/invoice-lines/${uuidv4()}/`;
  }

  getOne(id: string) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.invoiceStore.setLoading(true);

        return this.gqlService.getInvoice$(id);
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          const { workflow_details } = data;

          const invReq = {
            ...data,
            ...{
              cards: this.workflowToCards(workflow_details as WorkflowDetail[]),
              expense_amounts: this.mapExpenseAmountToObject(data.expense_amounts),
              line_items: JSON.parse(data?.line_items || '[]'),
              ocr_line_items: JSON.parse(data?.ocr_line_items || '[]'),
              po_reference: data.purchase_order_id,
            },
          } as unknown as InvoiceModel;

          if (data.reasons && data.reasons.length > 0) {
            invReq.decline_reason = Utils.unscrubUserInput(
              data.reasons.find((reason) => reason.note_type === NoteType.NOTE_TYPE_DECLINE_REASON)
                ?.message || ''
            );
            invReq.delete_reason = Utils.unscrubUserInput(
              data.reasons.find((reason) => reason.note_type === NoteType.NOTE_TYPE_DELETE_REASON)
                ?.message || ''
            );
          }

          this.invoiceStore.upsert(invReq.id, invReq);
        } else {
          this.overlayService.error(errors);
        }

        this.invoiceStore.setLoading(false);
      }),
      switchMap(() => {
        return this.invoiceQuery.selectEntity(id);
      })
    );
  }

  async goToInvoiceDetail(event: CellClickedEvent) {
    const id = event.data?.id;

    if (id) {
      await this.router.navigateByUrl(
        `${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}/${id}`
      );
    }
  }

  initialize() {
    return merge(
      this.organizationService.listIdOrganizations$().pipe(
        tap(() => {
          this.vendorOptions = this.organizationQuery
            .getAllVendors()
            .map(({ id, name }) => ({ label: name || '', value: id }));
        })
      ),
      this.purchaseOrdersService.get().pipe(
        tap(({ data: purchaseOrders }) => {
          if (purchaseOrders?.length) {
            this.purchaseOrderOptions = purchaseOrders.map(({ id, po_number }) => ({
              label: po_number,
              value: id,
            }));
          }
        })
      ),
      this.gqlService.getTrialInformation$().pipe(
        tap(({ data: trials }) => {
          if (trials?.length) {
            this.trialMonthClose = dayjs(trials[0].trial_month_close).format('YYYY-MM');
            this.trialStartDate = dayjs(trials[0].trial_start_date).format('YYYY-MM');
            this.trialEndDate = dayjs(trials[0].trial_end_date).format('YYYY-MM');
            this.accrualPeriodOptions = this.getAccrualPeriodOptions(
              trials[0].trial_start_date,
              trials[0].trial_end_date
            );
            this.servicePeriodOptions = this.getAccrualPeriodOptions(
              trials[0].trial_start_date,
              trials[0].trial_end_date
            );
          }
        })
      ),
      this.mainQuery.select('userList').pipe(
        tap((_users) => {
          _users.forEach((user: listUserNamesWithEmailQuery) => {
            this.users.set(user.sub, user);
          });
        })
      )
    );
  }

  initTrialChanges() {
    return combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ]);
  }

  invoiceLinesToCsv(data: Record<string, unknown>[], dataSource: DataSource) {
    let amountKey = 'amount';
    let descriptionKey = 'description';

    if (dataSource === DataSource.DATA_SOURCE_QUICKBOOKS_ONLINE) {
      amountKey = 'Amount';
      descriptionKey = 'Description';
    } else if (dataSource === DataSource.DATA_SOURCE_DYNAMICS365) {
      amountKey = 'netAmountIncludingTax';
    }

    const csvRows = [];

    if (dataSource === DataSource.DATA_SOURCE_AUXILIUS) {
      csvRows.push(
        'Auxilius AI (Beta) extraction: Data outside of scope for Auxilius QAQC processes; manual review recommended.',
        ''
      );
    }

    const headers = ['Description', 'Amount'];

    csvRows.push(headers.join(','));
    let total = 0;

    for (const row of data) {
      const values = headers.map((header) => {
        let value = String(row[header === 'Description' ? descriptionKey : amountKey]);

        if (header === 'Amount') {
          total += +value;
        }

        if (value.includes(',')) {
          value = `"${value}"`;
        }

        return value;
      });

      csvRows.push(values.join(','));
    }

    csvRows.push(`Total, ${total}`);

    return csvRows.join('\n');
  }

  isInInvoiceDetail(url: string) {
    return url.includes('invoices/');
  }

  mapExpenseAmountToObject(
    expense_amounts: {
      amount?: null | number | undefined;
      amount_type: string;
      contract_amount?: null | number | undefined;
      contract_curr?: null | string | undefined;
      exchange_rate?: MonthlyExchangeRate | null | undefined;
    }[]
  ) {
    const getAmount = (amountType: AmountType | string, is_vendor_currency_amount: boolean) => {
      const filteredAmounts = expense_amounts.filter((x) => x.amount_type === amountType)[0];
      const value = is_vendor_currency_amount
        ? filteredAmounts?.contract_amount
        : filteredAmounts?.amount;

      if (is_vendor_currency_amount) {
        return {
          contract_curr: filteredAmounts?.contract_curr
            ? (filteredAmounts.contract_curr?.replace('CURRENCY_', '') as Currency)
            : Currency.USD,
          is_vendor_currency_amount,
          type: amountType,
          value: value || 0,
        };
      } else {
        return {
          exchange_rate: filteredAmounts?.exchange_rate?.rate || 1,
          is_vendor_currency_amount,
          type: amountType,
          value: value || 0,
        };
      }
    };

    return {
      discount_total: getAmount(AmountType.AMOUNT_DISCOUNT, true),
      discount_total_trial_currency: getAmount(AmountType.AMOUNT_DISCOUNT, false),
      investigator_total: getAmount(AmountType.AMOUNT_INVESTIGATOR, true),
      investigator_total_trial_currency: getAmount(AmountType.AMOUNT_INVESTIGATOR, false),
      invoice_total: getAmount('AMOUNT_TOTAL', true),
      invoice_total_trial_currency: getAmount('AMOUNT_TOTAL', false),
      pass_thru_total: getAmount(AmountType.AMOUNT_PASSTHROUGH, true),
      pass_thru_total_trial_currency: getAmount(AmountType.AMOUNT_PASSTHROUGH, false),
      services_total: getAmount(AmountType.AMOUNT_SERVICE, true),
      services_total_trial_currency: getAmount(AmountType.AMOUNT_SERVICE, false),
    };
  }

  resetFiltersAfterTrialChange() {
    this.eventService.select$(EventType.TRIAL_CHANGED).subscribe(() => {
      this.filtersForm.setValue({
        accrualPeriod: [],
        does_invoice_mappings_match_total: null,
        has_prepaid: null,
        invoiceDate: '',
        is_deposit: null,
        purchase_order: [],
        search: '',
        services_period: [],
        vendors: [],
      });
    });
  }

  setAccrualPeriodsAndVendorFilter(accrualPeriods?: string[], vendor_ids?: string[]) {
    this.filtersForm.setValue({
      accrualPeriod: accrualPeriods ? accrualPeriods : [],
      does_invoice_mappings_match_total: null,
      has_prepaid: null,
      invoiceDate: '',
      is_deposit: null,
      purchase_order: [],
      search: '',
      services_period: [],
      vendors: vendor_ids ? vendor_ids : [],
    });
  }

  setGridApi(api: GridApi) {
    this.gridAPI = api;
  }

  setSelectedPurchaseOrder(options: string[]): void {
    this.selectedPurchaseOrder.set(options);
  }

  setSelectedServicePeriodOptions(options: string[]): void {
    this.selectedServicePeriodOptions.set(options);
  }

  setShowUnpaidInvoices(bool: boolean) {
    this.showUnpaidInvoices.set(bool);
  }

  // eslint-disable-next-line complexity,max-lines-per-function
  async update(invoice: InvoiceModel, showSuccessMessages = true, setLoading = true) {
    const cardPromises = [];

    if (setLoading) {
      this.invoiceStore.setLoading(true);
    }

    for (const { header, id: cardId, lines, note, status } of invoice.cards) {
      // update each card
      cardPromises.push(
        firstValueFrom(
          this.gqlService.updateWorkflowDetail$({
            id: cardId,
            properties: JSON.stringify({ properties: { header, lines, note, status } }),
          })
        )
      );
    }

    // send all the request in parallel
    const responses = await Promise.all(cardPromises);

    // check if everything is ok
    if (responses.every((x) => x.success)) {
      const { data, errors, success } = await firstValueFrom(
        this.gqlService.updateInvoice$({
          accrual_period: invoice.accrual_period,
          admin_review_reason: invoice.admin_review_reason,
          audit_record_create: invoice.audit_record_create,
          decline_reason: invoice.decline_reason,
          discount_amount: invoice.expense_amounts.discount_total?.value || 0,
          due_date: invoice.due_date,
          id: invoice.id,
          investigator_amount: invoice.expense_amounts.investigator_total?.value || 0,
          invoice_date: invoice.invoice_date,
          invoice_no: invoice.invoice_no,
          invoice_status: invoice.invoice_status,
          is_deposit: invoice.is_deposit,
          organization_id: invoice.organization.id,
          passthrough_amount: invoice.expense_amounts.pass_thru_total?.value || 0,
          payment_date: invoice.payment_date,
          payment_status: invoice.payment_status,
          po_reference: invoice.po_reference,
          previous_services_period: invoice.previous_services_period || null,
          service_amount: invoice.expense_amounts.services_total?.value || 0,
          services_period: invoice.services_period,
          total_amount: invoice.expense_amounts.invoice_total?.value || 0,
        })
      );

      if (success && data) {
        if (invoice.decline_reason && invoice.decline_reason.length > 0) {
          const {
            data: cData,
            errors: cErrors,
            success: cSuccess,
          } = await firstValueFrom(
            this.gqlService.createNote$({
              entity_id: invoice.id,
              entity_type: EntityType.INVOICE,
              message: Utils.scrubUserInput(invoice.decline_reason),
              note_type: NoteType.NOTE_TYPE_DECLINE_REASON,
            })
          );

          // eslint-disable-next-line max-depth
          if (cSuccess && cData && showSuccessMessages) {
            this.overlayService.success();
          }

          // eslint-disable-next-line max-depth
          if (!cSuccess) {
            this.overlayService.error(cErrors);
          }
        }

        if (invoice.admin_review_reason && invoice.admin_review_reason.length > 0) {
          const {
            data: cData,
            errors: cErrors,
            success: cSuccess,
          } = await firstValueFrom(
            this.gqlService.createNote$({
              entity_id: invoice.id,
              entity_type: EntityType.INVOICE,
              message: Utils.scrubUserInput(invoice.admin_review_reason),
              note_type: NoteType.NOTE_TYPE_ADMIN_REVIEW_REASON,
            })
          );

          // eslint-disable-next-line max-depth
          if (cSuccess && cData && showSuccessMessages) {
            this.overlayService.success();
          }

          // eslint-disable-next-line max-depth
          if (!cSuccess) {
            this.overlayService.error(cErrors);
          }
        }
      }

      if (success && data) {
        if (showSuccessMessages) {
          this.overlayService.success(MessagesConstants.INVOICE.SUCCESSFULLY_UPDATED);
        }

        this.invoiceStore.update(
          invoice.id,
          () =>
            ({
              ...data,
              cards: invoice.cards,
              decline_reason: invoice.decline_reason,
              expense_amounts: invoice.expense_amounts,
              po_reference: data.purchase_order_id,
            }) as unknown as InvoiceModel
        );

        if (setLoading) {
          this.invoiceStore.setLoading(false);
        }

        return true;
      }

      this.overlayService.error(errors);
    }

    const messages = flatten(responses.filter((x) => !x.success).map((x) => x.errors));

    this.overlayService.error(messages);

    if (setLoading) {
      this.invoiceStore.setLoading(false);
    }

    return false;
  }

  async updateInvoiceNote(invoice: InvoiceModel, message: string) {
    const note = invoice.notes?.[0];

    if (!note) {
      return null;
    }

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateNote$({
        id: note.id,
        message: message || '',
      })
    );

    if (success && data) {
      this.invoiceStore.update(invoice.id, ({ notes }) => {
        return {
          notes: notes?.map((n) => {
            if (n.id === note.id) {
              return { ...n, message };
            }

            return n;
          }),
        };
      });
      this.overlayService.success('Notes saved');

      return data;
    } else {
      this.overlayService.error(errors);

      return;
    }
  }

  userFormatter = (sub?: string): string => {
    const user = this.users.get(sub || '');

    return Utils.agUserFormatter(user as listUserNamesWithEmailQuery, this.authQuery.isAuxAdmin());
  };

  vendorValueSetter = (params: ValueSetterParams) => {
    const newValue = isString(params.newValue)
      ? this.vendorOptions.find(({ label }) => label === params.newValue)
      : params.newValue;

    const newVendor = this.organizationQuery.getVendor(newValue.value)[0];

    const data = cloneDeep(params.data);

    set(data, 'organization.currency', newVendor?.currency);
    set(data, 'organization.name', newValue.label);
    set(data, 'organization.id', newValue.value);
    params.node?.setData(data);

    return true;
  };

  workflowToCards(invoice_workflow: Array<WorkflowDetail>): InvoiceCard[] {
    return invoice_workflow.map((workflow) => {
      const obj = JSON.parse(workflow.properties || '') as { properties: InvoiceCard };

      return { ...obj.properties, id: workflow.id || '' };
    });
  }
}
